import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@illegally-parked/routes/Routes';
import { BinaryQuestionTemplate } from '@templates';
import { emailConfirmationQuestionData } from '../../../../constants/questionData';
import { useState } from 'react';
import { ErrorMessages } from '@types';
import { useParkingStore } from '@illegally-parked/store/store';
import { booleanSchema, OPTIONS_REQUIRED_ERROR, parseZodErrors } from '@utils';
import { ZodError } from 'zod';
import { useHandleSubmit } from '../../../../hooks/useHandleSubmit';

export const EmailConfirmationReport = () => {
  const navigate = useNavigate();
  const {
    wantConfirmationEmail,
    updateWantConfirmationEmail,
    getDataForSubmission,
    caseReferenceId,
  } = useParkingStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const questionData = emailConfirmationQuestionData;
  const [submitLoading, setSubmitLoading] = useState(false);
  const { mutateAsync: submitForm, isPending } = useHandleSubmit();
  const isOptionDefault = (
    optionLabel: 'Yes' | 'No',
    wantConfirmationEmail: boolean | null,
  ) => {
    switch (optionLabel) {
      case 'Yes':
        return wantConfirmationEmail === true;
      case 'No':
        return wantConfirmationEmail === false;
      default:
        return false;
    }
  };
  const options = questionData.options.map((option) => ({
    ...option,
    id: option.labelText,
    name: option.labelText,
    isLabelBold: false,
    isDefault: isOptionDefault(
      option.labelText as 'Yes' | 'No',
      wantConfirmationEmail,
    ),
  }));

  const handleChange = (result: string) => {
    setErrorMessages([]);
    updateWantConfirmationEmail(result === 'Yes');
  };

  const handleNext = async () => {
    setSubmitLoading(true);
    try {
      const validatedData = booleanSchema(
        questionData.key,
        OPTIONS_REQUIRED_ERROR.required.message,
      ).parse({
        [questionData.key]: wantConfirmationEmail,
      });
      updateWantConfirmationEmail(validatedData[questionData.key]);
      if (validatedData[questionData.key] === false) {
        if (caseReferenceId) {
          setErrorMessages([
            {
              path: 'wantConfirmationEmail',
              message:
                'You have already submitted this report. Please return to the homepage.',
            },
          ]);
          return;
        }
        const data = getDataForSubmission();
        await submitForm(data);
        navigate(RoutesList.CONFIRMATION);
      } else {
        navigate(RoutesList.YOUR_DETAILS);
      }
    } catch (error) {
      if (error instanceof ZodError) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error) => ({
            message: OPTIONS_REQUIRED_ERROR.required.message,
            path: error.path,
          })),
        );
      } else {
        setErrorMessages([
          {
            message: (error as Error).message,
            path: questionData.key,
          },
        ]);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const buttonNext = {
    label: wantConfirmationEmail === false ? 'Submit' : 'Next',
    onClick: handleNext,
    loading: isPending || submitLoading,
  };

  const buttonPrevious = {
    disabled: isPending || submitLoading,
    onClick: () => {
      navigate(RoutesList.DATETIME_DETAILS);
    },
  };

  return (
    <BinaryQuestionTemplate
      title={questionData.title}
      errorPath={questionData.key}
      errorMessages={errorMessages}
      options={options}
      onChange={handleChange}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
